import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';

import { catchError, EMPTY, Observable, throwError } from 'rxjs';

import { PlanLimitService } from '../services/plan-limit.service';
import {
  PlanLimitErrorInterface,
  PLAN_LIMIT_EXCEEDED_CODE
} from '../interfaces/plan-limit.interface';

/**
 * HTTP status code returned when a plan limit is exceeded.
 */
const PAYMENT_REQUIRED_STATUS = 402;

/**
 * HTTP interceptor that catches 402 Payment Required responses.
 *
 * @remarks
 * When the API returns a 402 with `error.code === 'PLAN_LIMIT_EXCEEDED'`,
 * this interceptor:
 * 1. Parses the error body
 * 2. Emits the error via PlanLimitService
 * 3. Returns EMPTY to suppress propagation to the calling component
 *
 * If the 402 is not a plan limit error, it propagates normally.
 */
export const planLimitInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const planLimitService = inject(PlanLimitService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === PAYMENT_REQUIRED_STATUS) {
        const planLimitError = extractPlanLimitError(error);

        if (planLimitError) {
          planLimitService.emit(planLimitError);
          return EMPTY;
        }
      }

      return throwError(() => error);
    })
  );
};

/**
 * Extracts a PlanLimitErrorInterface from the HTTP error response body.
 *
 * @param error - The HTTP error response
 * @returns The plan limit error if valid, null otherwise
 */
function extractPlanLimitError(error: HttpErrorResponse): PlanLimitErrorInterface | null {
  const body = error.error;

  if (!body || typeof body !== 'object') {
    return null;
  }

  const errorPayload = body.error;

  if (!errorPayload || errorPayload.code !== PLAN_LIMIT_EXCEEDED_CODE) {
    return null;
  }

  return errorPayload as PlanLimitErrorInterface;
}
